import { Check, LoaderCircle, Presentation } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils/cn";

type DemoRole = {
  readonly role: string;
  readonly label: string;
};

type DemoRoleDialogProps = {
  readonly open: boolean;
  readonly roles: readonly DemoRole[];
  readonly activeRole: string | null | undefined;
  readonly switchingRole: string | null | undefined;
  readonly onOpenChange?: (open: boolean) => void;
  readonly onSelect: (role: string | null, label: string) => void;
};

const DEMO_ROLE_COPY: Readonly<
  Record<string, { readonly title: string; readonly description: string }>
> = {
  통합관리자: {
    title: "그룹관리자",
    description: "관리 병동의 근무표와 원티드를 조회·관리할 수 있어요.",
  },
  근무표관리자: {
    title: "근무표관리자",
    description: "병동 근무표를 만들고 발행·관리할 수 있어요.",
  },
  근무자: {
    title: "근무자",
    description: "내 근무표를 확인하고 원티드를 작성할 수 있어요.",
  },
};

export default function DemoRoleDialog({
  open,
  roles,
  activeRole,
  switchingRole,
  onOpenChange,
  onSelect,
}: DemoRoleDialogProps) {
  const isInitialSelection = activeRole === undefined;
  const isSwitching = switchingRole !== undefined;
  const activeRoleItem = roles.find((item) => item.role === activeRole);
  const activeRoleLabel = activeRoleItem
    ? (DEMO_ROLE_COPY[activeRoleItem.label]?.title ?? activeRoleItem.label)
    : "본인 계정";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isInitialSelection && !isSwitching) onOpenChange?.(nextOpen);
      }}
    >
      <DialogContent
        aria-busy={isSwitching}
        hideCloseButton={isInitialSelection}
        overlayClassName="z-[9999] bg-black/35 backdrop-blur-[1px] motion-reduce:animate-none"
        className="z-[10000] w-[calc(100%-2rem)] max-w-sm gap-0 overflow-hidden rounded-xl border-border bg-card p-0 font-pretendard shadow-[0_24px_80px_rgba(15,23,42,0.24)] motion-reduce:animate-none"
        onEscapeKeyDown={(event) => {
          if (isInitialSelection || isSwitching) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isInitialSelection || isSwitching) event.preventDefault();
        }}
      >
        <DialogHeader className="gap-3 p-5 pb-3 text-left">
          <div className="flex size-10 items-center justify-center rounded-full bg-sig/10 text-sig">
            <Presentation aria-hidden="true" className="size-5" />
          </div>
          <div className="space-y-1.5">
            <DialogTitle className="text-lg font-semibold">
              시연할 권한을 선택하세요
            </DialogTitle>
            <DialogDescription>
              {isInitialSelection
                ? "선택한 역할의 시연 화면으로 이동합니다."
                : `현재 ${activeRoleLabel}으로 접속 중입니다.`}
            </DialogDescription>
          </div>
        </DialogHeader>

        <p className="sr-only" role="status" aria-live="polite">
          {isSwitching ? "시연 역할 전환 중…" : ""}
        </p>

        <div className="grid gap-2 p-5 pt-2">
          {roles.map((item) => {
            const isActive = activeRole === item.role;
            const isRoleSwitching = switchingRole === item.role;
            const roleCopy = DEMO_ROLE_COPY[item.label] ?? {
              title: item.label,
              description: "선택한 권한의 시연 화면을 확인할 수 있어요.",
            };

            return (
              <Button
                key={item.role}
                type="button"
                variant="outline"
                className={cn(
                  "h-auto min-h-[64px] w-full cursor-pointer justify-between rounded-xl px-4 py-2.5 disabled:cursor-default",
                  isActive &&
                    "border-transparent bg-sig/10 text-sig shadow-none hover:bg-sig/15 hover:text-sig focus-visible:border-sig/40 focus-visible:ring-sig/20"
                )}
                disabled={isSwitching}
                onClick={() => onSelect(item.role, item.label)}
              >
                <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
                  <span>{roleCopy.title}</span>
                  <span
                    className={`text-xs font-normal ${
                      isActive ? "text-sig/70" : "text-muted-foreground"
                    }`}
                  >
                    {roleCopy.description}
                  </span>
                </span>
                {isRoleSwitching ? (
                  <LoaderCircle
                    aria-hidden="true"
                    className="size-4 animate-spin motion-reduce:animate-none"
                  />
                ) : (
                  isActive && <Check aria-hidden="true" className="size-4" />
                )}
              </Button>
            );
          })}

          <Button
            type="button"
            variant="outline"
            className={cn(
              "mt-1 h-auto min-h-[64px] w-full cursor-pointer justify-between rounded-xl px-4 py-2.5 disabled:cursor-default",
              activeRole === null &&
                "border-transparent bg-sig/10 text-sig shadow-none hover:bg-sig/15 hover:text-sig focus-visible:border-sig/40 focus-visible:ring-sig/20"
            )}
            disabled={isSwitching}
            onClick={() => onSelect(null, "본인 계정")}
          >
            <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
              <span>본인 계정으로 로그인</span>
              <span
                className={`text-xs font-normal ${
                  activeRole === null
                    ? "text-sig/70"
                    : "text-muted-foreground"
                }`}
              >
                현재 계정에 부여된 실제 권한으로 로그인합니다.
              </span>
            </span>
            {switchingRole === null ? (
              <LoaderCircle
                aria-hidden="true"
                className="size-4 animate-spin motion-reduce:animate-none"
              />
            ) : (
              activeRole === null && (
                <Check aria-hidden="true" className="size-4" />
              )
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
